import React, { useEffect, useState, Ref } from 'react';
import { path } from 'ramda';
import { Row, Col, Select, Radio } from 'antd';
import styled from 'styled-components';
import { HelpInfo, ServiceType, Situation } from 'types';

import { useIntl } from 'common/config/i18n';
import useWindowDimensions from 'hooks/use-dimensions';
import { isFirefox } from 'common/utils/browser';
import { htmlParser } from 'common/utils/html-parser';

const { Option } = Select;

export type Props = {
  data?: HelpInfo[];
  situations: Situation[];
  defaultSituationId?: number;
  disabled: boolean;
  headerHeight: number;
  elemRef?: Ref<HTMLDivElement>;
  serviceTypes: ServiceType[];
  defaultServiceTypeID?: number;
  getDataMessageAssisant: ({
    situationIDValue,
    subFamilyValue,
    serviceTypeIDValue,
  }: {
    situationIDValue?: number;
    subFamilyValue?: string;
    serviceTypeIDValue?: number;
  }) => void;
  handleCopyText: (longMessage: string, shortMessage?: string) => void;
};
type CustomProps = {
  disabled: boolean;
  height?: number;
};
type WrapperProps = {
  height: number;
};

const Wrapper = styled.div<WrapperProps>`
  height: ${(props) => props.height}px;
`;
const Label = styled.div<CustomProps>`
  font-size: ${path(['theme', 'font', 'size', 'caption'])};
  align-self: center;
  color: ${(props) =>
    props.disabled ? path(['theme', 'colors', 'grey', 'dark']) : path(['theme', 'colors', 'common', 'black'])};
`;
const Header = styled.div<CustomProps>`
  display: flex;
  background-color: ${(props) =>
    props.disabled ? path(['theme', 'colors', 'grey', 'dark']) : path(['theme', 'colors', 'secondary', 'main'])};
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
`;
const Title = styled.div`
  color: white;
  padding: 20px 0;
  text-align: center;
  width: 100%;
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: ${path(['theme', 'font', 'weight', 'medium'])};
`;
const Content = styled.div<CustomProps>`
  border: ${(props) =>
      props.disabled ? path(['theme', 'colors', 'grey', 'dark']) : path(['theme', 'colors', 'secondary', 'main'])}
    2px solid;
  background-color: ${(props) =>
    props.disabled ? path(['theme', 'colors', 'grey', 'lighter']) : path(['theme', 'colors', 'common', 'white'])};
  border-top: none;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  padding: 20px 10px;
  overflow-y: scroll;
  height: ${(props) => props.height}px;
`;
const SentencesWrapper = styled.div`
  margin-top: 1rem;
`;
const SentencesHeader = styled.div`
  background-color: ${path(['theme', 'colors', 'grey', 'light'])};
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  min-height: 2rem;
`;
const SentenceTitle = styled.div<CustomProps>`
  color: ${(props) =>
    props.disabled ? path(['theme', 'colors', 'grey', 'dark']) : path(['theme', 'colors', 'common', 'black'])};
  padding: 10px;
  width: 100%;
  text-align: center;
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: ${path(['theme', 'font', 'weight', 'medium'])};
`;
const SentenceContent = styled.div<CustomProps>`
  border: ${path(['theme', 'colors', 'grey', 'light'])} 2px solid;
  border-top: none;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  padding: 10px;
  text-align: left;
  color: ${(props) =>
    props.disabled ? path(['theme', 'colors', 'grey', 'dark']) : path(['theme', 'colors', 'common', 'black'])};
`;

const SortArray = (x: { subFamily: string }, y: { subFamily: string }) => {
  return x.subFamily.localeCompare(y.subFamily);
};

const MessageAssistant = ({
  data,
  situations,
  defaultSituationId,
  disabled,
  headerHeight,
  elemRef,
  serviceTypes,
  defaultServiceTypeID,
  getDataMessageAssisant,
  handleCopyText,
}: Props): React.ReactElement => {
  const { t } = useIntl();
  const { height: windowHeight } = useWindowDimensions();

  const [height, setHeight] = useState<number>(0);
  const [subFamily, setSubFamily] = useState<string | undefined>(undefined);
  const [situationID, setSituationID] = useState<number | undefined>(undefined);
  const [serviceTypeID, setServiceTypeID] = useState<number | undefined>(undefined);
  const [situationCategory, setSituationCategory] = useState<number | undefined>(undefined);
  const [subFamilies, setSubFamilies] = useState<Array<{ subFamily: string; id: number }> | undefined>(undefined);
  const [helpInfosData, setHelpInfosData] = useState<HelpInfo[] | undefined>(undefined);

  useEffect(() => {
    setHeight(isFirefox ? windowHeight - headerHeight - 30 : windowHeight - headerHeight + 150);
  }, [windowHeight, headerHeight]);

  useEffect(() => {
    const defaultSituation = situations.find((situation) => situation.id === defaultSituationId);

    if (defaultSituation && defaultServiceTypeID) {
      setServiceTypeID(defaultServiceTypeID);
      setSituationID(defaultSituation.id);
      setSituationCategory(defaultSituation?.situationCategory?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [situations, defaultSituationId, defaultServiceTypeID]);

  useEffect(() => {
    setHelpInfosData(data);
    const subFamiliesData = data
      ?.filter((item) => item.subFamily)
      ?.sort(SortArray)
      .map((item) => ({ subFamily: item.subFamily, id: item.id }));
    const subFamiliesFiltered = subFamiliesData?.filter(
      (item, index) => subFamiliesData?.findIndex((sub) => sub.subFamily === item.subFamily) === index
    );
    setSubFamilies(subFamiliesFiltered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const filteredSituations = situations.filter((situation) => situation?.situationCategory?.id === situationCategory);

  return (
    <Wrapper ref={elemRef} height={height}>
      <Header disabled={disabled}>
        <Title>{t('sendCommunication', 'INFORMATION_HELP_H3')}</Title>
      </Header>
      <Content disabled={disabled} height={height - 75}>
        {serviceTypes.length > 1 && (
          <Row align="middle" gutter={10}>
            <Col span="6">
              <Label disabled={disabled}>{t('form', 'circulationType')}</Label>
            </Col>
            <Col span="18">
              <Select
                dropdownStyle={{ minWidth: '20%' }}
                style={{ minWidth: '250px', borderRadius: '5px', marginLeft: '10px' }}
                popupMatchSelectWidth
                disabled={disabled}
                value={serviceTypeID}
                onChange={(value) => {
                  setServiceTypeID(value);
                  getDataMessageAssisant({
                    serviceTypeIDValue: value,
                    situationIDValue: situationID,
                    subFamilyValue: subFamily,
                  });
                }}
                allowClear
              >
                {serviceTypes.map((serviceType) => (
                  <Option value={serviceType.id} key={serviceType.id}>
                    {serviceType.serviceTypeName}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        )}

        <Row style={{ marginTop: '0.5rem' }}>
          <Radio.Group
            style={{ marginLeft: '1rem' }}
            disabled={disabled}
            value={situationCategory}
            onChange={(e) => {
              setSituationCategory(e.target.value);
              setSituationID(undefined);
              setSubFamily(undefined);
              setHelpInfosData(undefined);
              setSubFamilies(undefined);
            }}
          >
            <Radio value={2}>{t('process', 'operational')}</Radio>
            <Radio value={1}>{t('process', 'pre-operational')}</Radio>
          </Radio.Group>
        </Row>

        <Row align="middle" gutter={10} style={{ marginTop: '0.5rem', width: '100%' }}>
          <Col span="6">
            <Label disabled={disabled}>{t('form', 'situationsProcess')}</Label>
          </Col>
          <Col span="18">
            <Select
              dropdownStyle={{ minWidth: '20%' }}
              style={{ minWidth: '250px', borderRadius: '5px', marginLeft: '10px' }}
              popupMatchSelectWidth
              disabled={disabled}
              value={situationID}
              onChange={(value) => {
                setSituationID(value);
                setSubFamily(undefined);
                getDataMessageAssisant({
                  serviceTypeIDValue: serviceTypeID,
                  situationIDValue: value,
                });
              }}
              allowClear
            >
              {filteredSituations.map((x) => (
                <Option value={x.id} key={x.id}>
                  {x?.situationCategory?.name} -{x?.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row align="middle" gutter={10} style={{ marginTop: '0.5rem', width: '100%' }}>
          <Col span="6">
            <Label disabled={disabled}>{t('form', 'SUB_FAMILY')}</Label>
          </Col>
          <Col span="18">
            <Select
              showSearch
              allowClear
              dropdownStyle={{ minWidth: '20%' }}
              style={{ minWidth: '250px', borderRadius: '5px', marginLeft: '10px' }}
              popupMatchSelectWidth
              disabled={disabled}
              value={subFamily}
              onChange={(value) => {
                setSubFamily(value);
                getDataMessageAssisant({
                  serviceTypeIDValue: serviceTypeID,
                  situationIDValue: situationID,
                  subFamilyValue: value,
                });
              }}
            >
              {subFamilies && (
                <>
                  {subFamilies.map((subFamily) => (
                    <Option key={subFamily.id} value={subFamily.subFamily}>
                      {subFamily.subFamily}
                    </Option>
                  ))}
                </>
              )}
            </Select>
          </Col>
        </Row>
        {helpInfosData && (
          <>
            {helpInfosData.map((items, id) => (
              <SentencesWrapper
                onClick={() => {
                  if (!disabled) handleCopyText(items.longMessage, items.shortMessage);
                }}
                key={id}
              >
                <SentencesHeader>
                  <SentenceTitle disabled={disabled}>{items.subFamily}</SentenceTitle>
                </SentencesHeader>
                <SentenceContent disabled={disabled} className="ql-editor">
                  {htmlParser(items.longMessage)}
                </SentenceContent>
              </SentencesWrapper>
            ))}
          </>
        )}
      </Content>
    </Wrapper>
  );
};

export default MessageAssistant;
